/**
 * Per-recruiter localStorage cache for TA Action Board payloads.
 *
 * Always addressed as `window.localStorage`, never as the bare global: Node 25
 * ships its own `localStorage` global, which shadows the one the test DOM
 * provides and is unusable without --localstorage-file. Going through `window`
 * is identical in the browser and keeps the tests honest on any Node version.
 *
 * Selecting a recruiter renders their cached board instantly; a fresh Lever
 * pull only happens on the first-ever visit or via the explicit Refresh
 * button (triggered runs only — no automatic refetch, product decision).
 * Cached candidate data lives in the signed-in recruiter's own browser.
 */
import type { BoardResponse, TrackingMap } from '../../types/taActionBoard';

/** Bump when the cached shape changes to invalidate old entries. */
// v4: sla.awaitingCandidate + phone on SLA items + ordered `stages` list
// v5: FeedbackItem swapped noteDraft for emailDraft (subject/body/text) — a v4
// payload has no email to show in the Archive modal.
// v6: FeedbackItem gained otherOpenOpportunities.
// v7: sla.snoozed / sla.snoozedCount, the `feedback-missing` classification and
// the `invited-no-reply` stuck state — a v6 payload has neither.
// v8: the 🏁 finalRounds section per role — a v7 payload has none.
// v9: emailDraft gained emailError (the Email Library became the only source of
// rejection wording) — a v8 payload holds a draft built from deleted in-code copy.
// v10: per-role `notes` map (Lever candidate notes) — a v9 payload has none, so the
// notes chip would never appear until the cache expired.
// v11: that `notes` map is always EMPTY now — notes are read when the 📝 modal opens, not on
// every board build (Oct 2026). Nothing would break on a v10 payload, since the chip stopped
// reading the map at all; the bump is so nobody is left looking at counts that no longer exist
// anywhere else, and so the cached payloads on the team's laptops match what the proxy sends.
const CACHE_VERSION = 11;

/**
 * Fields a cached FeedbackItem must carry for the board and its modals to render.
 *
 * The version number above relies on someone remembering to bump it, and four times
 * running that failed — the last one white-screened the board because a cached item had
 * no `otherOpenOpportunities` and the modal read `.length` off undefined. This is the
 * belt: a cached payload missing any of these is discarded regardless of its version.
 */
const REQUIRED_FEEDBACK_FIELDS = [
  'opportunityId',
  'name',
  'decision',
  'bullets',
  'emailDraft',
  'otherOpenOpportunities',
] as const;

/**
 * Fields the DRAFT inside a feedback item must carry.
 *
 * Separate from the list above because the bug that needed it lived one level down: the item still
 * had an `emailDraft`, so every check passed, while the draft inside it had been built by the
 * in-code rejection templates that no longer exist. `emailError` only exists on drafts built after
 * the Email Library became the only source, which makes its absence a reliable "this is old".
 */
const REQUIRED_DRAFT_FIELDS = ['emailError'] as const;

function feedbackShapeIsCurrent(board: BoardResponse): boolean {
  // Same belt for role-level sections: a cached role without the finalRounds
  // section would white-screen the 🏁 tab regardless of the version number.
  if (!(board.roles || []).every((r) => r.finalRounds)) return false;
  // A stage reporting `linkFound: false` WITHOUT a `linkMissing` key predates the reason
  // labels: rendering it falls back to a bare "no interview link", which is the exact wording
  // that hid a mis-staged/expired link from the recruiter who had just set one up. Discard it
  // rather than mislabel it. (`linkFound: true` needs no reason, so its absence is fine there.)
  const stagesCurrent = (board.roles || []).every((r) =>
    (r.firstInterviews?.stages || []).every(
      (s) => s.linkFound || Object.prototype.hasOwnProperty.call(s, 'linkMissing')
    )
  );
  if (!stagesCurrent) return false;
  const items = (board.roles || []).flatMap((r) => r.feedback || []);
  // Nothing to validate is not a reason to throw the cache away.
  if (items.length === 0) return true;
  return items.every((item) => {
    if (!REQUIRED_FEEDBACK_FIELDS.every((f) => Object.prototype.hasOwnProperty.call(item, f))) {
      return false;
    }
    // A null draft is legitimate (a hire, or no feedback to base one on) — only inspect a real one.
    const draft = (item as { emailDraft?: unknown }).emailDraft;
    if (!draft || typeof draft !== 'object') return true;
    return REQUIRED_DRAFT_FIELDS.every((f) => Object.prototype.hasOwnProperty.call(draft, f));
  });
}

const CACHE_PREFIX = 'ta-board:';
const cacheKey = (email: string) => `${CACHE_PREFIX}${email.toLowerCase()}`;

/**
 * Throw away every cached board, for all recruiters.
 *
 * The board caches the WHOLE payload and deliberately never refetches, so anything that changes
 * what the server would compute has to say so explicitly. Creating an interview link is exactly
 * that: `linkFound` is baked into the cached payload, so a link set up after a board was cached
 * left the Scheduling tab reading "no interview link" through navigation and full reloads, with
 * only the Refresh button able to clear it.
 *
 * All recruiters, not just the signed-in one: links are keyed to a POSTING, and whoever owns that
 * posting is usually not the person configuring the link. Over-clearing costs one Lever pull;
 * under-clearing shows a stale "no link" indefinitely.
 */
export function clearAllBoardCaches(): void {
  try {
    const keys = Object.keys(window.localStorage).filter((k) => k.startsWith(CACHE_PREFIX));
    keys.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    // storage unavailable — the server-side revision check below is the real guard
  }
}

export interface CachedBoard {
  board: BoardResponse;
  tracking: TrackingMap;
  /**
   * The proxy build + draft format this payload came from (`GET /payload-version`). Compared before
   * the cache is used, so a deploy that changes what the server would send discards it — the version
   * integer above relies on someone remembering, and it failed five times running.
   */
  serverVersion?: string;
  /**
   * opportunityId → what happened to them this session ("Archived", "Moved to
   * Case", …). Cards stay visible but muted until the next fresh Lever pull,
   * so the recruiter sees what they already managed without refreshing.
   */
  handled: Record<string, string>;
  /** ISO timestamp of the Lever pull that produced this board */
  fetchedAt: string;
}

/**
 * Compare a cached payload's version stamp against what the proxy reports NOW.
 *
 * The string is `<build>:<promptVersion>:<linksRevision>` (`GET /payload-version`). The first two
 * segments compare strictly. The third is an ISO timestamp (it contains colons — hence the
 * rest-join) with two sentinel values, and this function is where "fail safe" is defined:
 *
 * - server says `unknown` (the proxy could not read the revision even after a retry): the segment
 *   is treated as MATCHING. A transient Firestore blip must not trigger a full board rebuild
 *   (hundreds of Lever calls); the build/prompt segments still protect against a stale deploy.
 * - the CACHED stamp says `unknown` but the server now has a real revision: MISMATCH. The payload
 *   was written without a verifiable revision, and now that one is knowable, it must be proven.
 * - anything else: strict equality, including `none` (no link ever written — stable and
 *   comparable).
 *
 * A missing cached stamp is a mismatch: it predates version stamping entirely.
 */
export function versionsMatch(cached: string | null | undefined, server: string): boolean {
  if (!cached) return false;
  const parse = (v: string) => {
    const [build, prompt, ...rest] = v.split(':');
    return { build, prompt, links: rest.join(':') };
  };
  const c = parse(cached);
  const s = parse(server);
  if (c.build !== s.build || c.prompt !== s.prompt) return false;
  if (s.links === 'unknown') return true;
  return c.links === s.links;
}

/**
 * @param serverVersion what the proxy currently reports. Pass it and a payload from a different
 *   build is discarded; pass null (the check itself failed) and the cache is used anyway — a version
 *   endpoint being down must not stop a recruiter seeing their board.
 */
export function readBoardCache(email: string, serverVersion?: string | null): CachedBoard | null {
  try {
    const raw = window.localStorage.getItem(cacheKey(email));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedBoard & { v?: number };
    if (parsed.v !== CACHE_VERSION || !parsed.board?.roles || !parsed.fetchedAt) return null;
    if (serverVersion && !versionsMatch(parsed.serverVersion, serverVersion)) return null;
    if (!feedbackShapeIsCurrent(parsed.board)) return null;
    return {
      board: parsed.board,
      serverVersion: parsed.serverVersion,
      tracking: parsed.tracking || {},
      handled: parsed.handled || {}, // absent in pre-handled caches — tolerated
      fetchedAt: parsed.fetchedAt,
    };
  } catch {
    return null; // corrupt entry or storage unavailable — treat as no cache
  }
}

export function writeBoardCache(
  email: string,
  board: BoardResponse,
  tracking: TrackingMap,
  fetchedAt: string,
  handled: Record<string, string> = {},
  serverVersion?: string | null
): void {
  try {
    window.localStorage.setItem(
      cacheKey(email),
      JSON.stringify({ v: CACHE_VERSION, board, tracking, handled, fetchedAt, serverVersion })
    );
  } catch {
    // quota exceeded / private mode — the board still works, just without instant loads
  }
}

/**
 * The handled map minus anything an unfinished action marked.
 *
 * Exported from here rather than written inline in the view because it is a
 * rule about what the CACHE may contain: a marker is a record of something that
 * happened, and a pending action has not happened yet. Returns the original
 * object when nothing is pending, which is the overwhelmingly common case.
 */
export function omitPending(
  handled: Record<string, string>,
  pendingIds: Set<string>
): Record<string, string> {
  if (pendingIds.size === 0) return handled;
  const out: Record<string, string> = {};
  for (const [id, label] of Object.entries(handled)) if (!pendingIds.has(id)) out[id] = label;
  return out;
}
